import Loader from 'components/loaderComponent';
import { useModalParams } from 'components/modal/useModalManager';
import { MyAppointmentsModal } from './MyAppointmentsModal';
import { SharedMediaModal } from 'app/my-patients/components/SharedMediaModal';
import CancelAppointmentModalProvider from 'app/appointments/components/CancelAppointmentModalProvider';
import {
  AppointmentTypeEnum,
  useGetFhirEncountersByProviderQuery,
  useGetFhirPatientsRelatedToProviderCountQuery,
} from 'graphql/generated/remote-schema-hasura';
import {
  PARAM_MODALS_IDENTIFIERS,
  pageIds,
  componentIds,
} from 'utilities/constants';
import { useGetPage } from 'hooks/useGetPage';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import MyVisitSummaryModal from './MyVisitSummaryModal';
import { NoAppointmentHistory } from 'app/my-appointments/components/NoAppointmentHistory';
import { AppointmentHistoryProvider } from 'app/my-appointments/components/AppointmentHistory';
import { UpcomingAppointments } from 'app/my-appointments/components/UpcomingAppointments';
import PastAppointmentWrapper from './past-appointments/PastAppointmentWrapper';
import { UnfilledSummaryAppointments } from './UnfilledSummaryAppointments';
import {
  MY_APPOINTMENTS,
  MY_APPOINTMENTS_COMPLETE_MY_VISIT_SUMMARY,
  MY_APPOINTMENTS_MY_VISIT_SUMMARY,
  RECOMMEND_APPOINTMENT,
} from 'utilities/routes';
import FillInAppointmentModal from './FillInAppointment';
import { useTimer } from 'hooks/useTimer';
import React, { useEffect, useMemo, useState } from 'react';
import Alert from 'components/alertComponent';
import { interpolateVariables } from 'utilities/functions';
import ButtonComponent from 'components/button/buttonComponent';
import CustomTooltip from 'components/CustomTooltip';
import AppointmentRequests from './appointment-requests';
import AcceptAiModal from './vonage-room-v2/components/AcceptAIModal';
import { useGetComponent } from 'hooks/useGetComponent';
import { Step } from 'react-joyride';
import Tour from 'components/Tour';
import { AppTourTypes, EncounterStatuses } from 'graphql/generated/hasura';

const SHOW_SECONDS = 5;

export const ProviderAppointmentsMainPage = () => {
  const navigate = useNavigate();
  const [hasUpcomingAppointments, setHasUpcomingAppointments] = useState(false);
  const [ready, setReady] = useState(false);
  const onLoaded = () => {
    if (ready) return null;

    setReady(true);
    return null;
  };

  const { data, loading } = useGetFhirPatientsRelatedToProviderCountQuery();
  const patientsCount = useMemo(() => data?.getFHIRPatientsById?.total, [data]);
  const [isCheckboxChecked, setIsCheckboxChecked] = React.useState(false);

  const handleNavigateCompleteSummary = (appointmentId: string) => {
    navigate(`${MY_APPOINTMENTS_COMPLETE_MY_VISIT_SUMMARY}/${appointmentId}`);
  };

  const location = useLocation();

  const { data: localeTour, loading: loadingTours } = useGetComponent({
    locale: 'en',
    componentId: componentIds.PROVIDER_APPOINTMENT_TOUR,
  });

  const { isOpen: isAppointmentVideoModal } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.VIDEO_APPOINTMENT_MODAL,
  );
  const { isOpen: isAppointmentChatModal } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.CHAT_APPOINTMENT_MODAL,
  );
  const { isOpen: isMyVisitSummaryOpen } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.MY_VISIT_SUMMARY_MODAL_PROVIDER,
  );

  const { isOpen: isFillInSummaryModal } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.FILL_IN_SUMMARY_MODAL,
  );

  const handleMyVisitSummaryClick = (appointmentId: string) => {
    navigate(`${MY_APPOINTMENTS_MY_VISIT_SUMMARY}/${appointmentId}`, {
      state: { appointmentId },
    });
  };

  const handleSuggestAppointment = () => {
    return navigate(`${MY_APPOINTMENTS}/${RECOMMEND_APPOINTMENT}`);
  };

  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment-id');
  const { isOpen: isMediaModalOpen } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.MEDIA_LIBRARY_MODAL,
  );
  const { isOpen: isCancelAppointmentOpen } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.CANCEL_APPOINTMENT_MODAL_PROVIDER_ID,
  );

  const { data: locale, loading: localeLoading } = useGetPage({
    locale: 'en',
    pageId: pageIds.MY_APPOINTMENTS,
  });

  const { data: summaryData } = useGetFhirEncountersByProviderQuery({
    variables: {
      status: EncounterStatuses.InProgress,
      limit: 1,
      offset: 0,
    },
  });

  const summaryNotCompleted = !!summaryData?.getFHIREncountersByProvider?.total;

  const steps: Array<Step> = useMemo(
    () =>
      [
        hasUpcomingAppointments && {
          disableBeacon: true,
          target: '[data-tour="upcoming-appointments"]',
          title: localeTour?.upcomingAppointments?.title,
          placement: 'right-start',
          content: localeTour?.upcomingAppointments?.content,
        },
        summaryNotCompleted && {
          disableBeacon: true,
          target: '[data-tour="visit-summaries"]',
          title: localeTour?.visitSummaries?.title,
          placement: 'top-start',
          content: localeTour?.visitSummaries?.content,
          data: {
            className: 'mb-0',
          },
        },
        {
          disableBeacon: true,
          placement: 'bottom-start',
          target: '[data-tour="past-appointments"]',
          title: localeTour?.emptyState?.title,
          content: localeTour?.emptyState?.content,
        },
      ].filter(Boolean) as Array<Step>,
    [localeTour, hasUpcomingAppointments, summaryNotCompleted],
  );

  const {
    temporalyDisableResendActions: showAlert,
    setTemporalyDisableResendActions: setShowAlert,
    setTimeLeft,
  } = useTimer();

  useEffect(() => {
    if (!location.state?.completedSummaryPatient) return;

    setShowAlert(true);
    setTimeLeft(SHOW_SECONDS);
  }, [setShowAlert, setTimeLeft, location.state?.completedSummaryPatient]);

  if (localeLoading || loadingTours) {
    return <Loader />;
  }

  if (
    (isAppointmentVideoModal || isAppointmentChatModal) &&
    !isCheckboxChecked
  ) {
    return (
      <AcceptAiModal
        setIsCheckboxChecked={setIsCheckboxChecked}
        isOpen={isAppointmentChatModal || isAppointmentVideoModal}
      />
    );
  }

  if (isAppointmentChatModal) {
    return (
      <MyAppointmentsModal
        appointmentType={AppointmentTypeEnum.Chat}
        appointmentId={appointmentId}
        isPatient={false}
      />
    );
  }
  if (isMediaModalOpen) {
    return <SharedMediaModal />;
  }

  if (isAppointmentVideoModal) {
    return (
      <MyAppointmentsModal
        appointmentType={AppointmentTypeEnum.Video}
        appointmentId={appointmentId}
        isPatient={false}
      />
    );
  }

  if (isMediaModalOpen) {
    return <SharedMediaModal />;
  }

  if (isMyVisitSummaryOpen) {
    return <MyVisitSummaryModal />;
  }

  return (
    <>
      {onLoaded()}
      {isCancelAppointmentOpen && <CancelAppointmentModalProvider />}
      {isFillInSummaryModal && <FillInAppointmentModal />}
      <div
        className="px-0 pt-[30px] desktop:pt-0 desktop:px-7"
        data-testid="my-appointsmets-page"
      >
        {showAlert && (
          <Alert
            type="positive"
            text={interpolateVariables(locale?.completeSuccessfullyMessage, {
              patientName: location.state?.completedSummaryPatient,
            })}
          />
        )}
        <div className="flex flex-col gap-[30px]">
          <div className="flex desktop:flex-row w-full justify-between items-start p-0 gap-2.5 desktop:gap-[30px] desktop:mb-[34px]">
            <div className="flex flex-1 flex-col items-start">
              <p className="text-h2 text-dark-gray font-medium desktop:text-h1">
                {locale.myAppointmentsTabName}
              </p>
              <NoAppointmentHistory
                noAppointmentsText={locale.noAppointmentsText}
              />
            </div>

            <div className="flex items-center gap-2.5 ml-auto">
              <p className="text-sm text-med-gray-3 font-semibold text-right whitespace-pre-wrap">
                {
                  'Advise an appointment visit.\nThe patient will receive a notification to schedule their appointment'
                }
              </p>
              <ButtonComponent
                onClick={handleSuggestAppointment}
                testID="request-appointment"
                disabled={loading || !patientsCount}
                dataTooltipId={!patientsCount && 'request-appointment'}
              >
                Suggest appointment
              </ButtonComponent>
            </div>
          </div>
          <>
            <AppointmentRequests />
            <UpcomingAppointments
              setHasUpcomingAppointments={setHasUpcomingAppointments}
            />
            <div data-tour="visit-summaries" className="w-full">
              <UnfilledSummaryAppointments
                handleNavigateCompleteSummaryModal={
                  handleNavigateCompleteSummary
                }
              />
            </div>
            <AppointmentHistoryProvider
              handleMyVisitSummaryClick={handleMyVisitSummaryClick}
              handleNavigateCompleteSummaryModal={handleNavigateCompleteSummary}
            >
              <PastAppointmentWrapper />
            </AppointmentHistoryProvider>
          </>
        </div>
      </div>

      <CustomTooltip
        place="left"
        className="max-w-[180px]"
        id="request-appointment"
        content="This option will be available when you have at least one assigned patient."
      />

      {ready && !loadingTours && (
        <Tour
          skipValidate
          allowOnceByName={AppTourTypes.ProviderAppointments}
          steps={steps}
          scrollOffset={1}
        />
      )}
    </>
  );
};
